// var scrollAnimate = require('base/components/scrollAnimate');

/**
 * Validate whole form. Requires `this` to be set to form object
 * @param {jQuery.event} event - Event to be canceled if form is invalid.
 * @returns {boolean} - Flag to indicate if form is valid
 */
function validateForm(event) {
    var valid = true;
    if (this.checkValidity && !this.checkValidity()) {
        // safari
        valid = false;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        $(this).find('input, select').each(function () {
            if (!this.validity.valid) {
                $(this).trigger('invalid', this.validity);
            }
        });
    }
    return valid;
}

/**
* Validate the input value whether it contains script tag or not
* @param {jQuery} input - form input field value
* @returns {string} - returns the string by removing the script tag from the input
*/
function sanitizeInput(input) {
    if (input) return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    return '';
}

/**
 * Check whether the field is a phone or fax field.
 * @param {HTMLElement|jQuery} input - field to inspect
 * @returns {boolean} true when the field is a phone/fax input
 */
function isPhoneField(input) {
    var $input = $(input);

    return $input.hasClass('js-phone-number')
        || $input.hasClass('js-fax-number')
        || $input.hasClass('js-phone')
        || $input.hasClass('shippingPhoneNumber')
        || $input.hasClass('shipping-phone-number');
}

/**
 * Get the numeric digit count for a phone-like input value.
 * @param {string} inputValue - raw input value
 * @returns {number} digit count
 */
function getPhoneDigitCount(inputValue) {
    return (inputValue || '').replace(/\D/g, '').length;
}

/**
 * Determine whether a phone-like field contains invalid characters.
 * @param {HTMLElement|jQuery} input - field to inspect
 * @returns {boolean} true when the value contains characters outside the allowed phone set
 */
function hasPhoneCharacterError(input) {
    var $input = $(input);
    var inputValue = String($input.val() || '').trim();
    var allowedCharsOnly = /^[0-9()+\s.-]+$/;

    if (!inputValue) {
        return false;
    }

    return !allowedCharsOnly.test(inputValue);
}

/**
 * Determine whether a phone-like field violates min/max digit constraints.
 * @param {HTMLElement|jQuery} input - field to inspect
 * @returns {boolean} true when digit length is outside configured bounds
 */
function hasPhoneLengthConstraintError(input) {
    var $input = $(input);
    var inputValue = $input.val() || '';
    var digitCount = getPhoneDigitCount(inputValue);
    var minLength = Number($input.attr('minlength'));
    var maxLength = Number($input.attr('maxlength'));

    if (!digitCount) {
        return false;
    }

    if (minLength && digitCount < minLength) {
        return true;
    }

    if (maxLength && digitCount > maxLength) {
        return true;
    }

    return false;
}

/**
 * Safely read the browser validation message.
 * @param {HTMLElement|jQuery} input - invalid field
 * @returns {string} native validation message or empty string when browser throws
 */
function getNativeValidationMessage(input) {
    try {
        return input.validationMessage;
    } catch (error) {
        return '';
    }
}

/**
 * Safely read a validity state flag.
 * @param {HTMLElement|jQuery} input - field to inspect
 * @param {string} propertyName - ValidityState property name
 * @returns {boolean} validity flag value
 */
function getValidityFlag(input, propertyName) {
    try {
        return Boolean(input.validity && input.validity[propertyName]);
    } catch (error) {
        return false;
    }
}

/**
 * Resolve the validation message for an invalid field.
 * @param {HTMLElement|jQuery} input - invalid field
 * @returns {string} validation message to display
 */
function getValidationMessage(input) {
    var $input = $(input);
    var validationMessage = getNativeValidationMessage(input);
    var hasParseErrorMessage = $input.data('parse-error') || $input.data('pattern-mismatch');
    var hasPhoneParseError = isPhoneField(input)
        && hasParseErrorMessage
        && hasPhoneCharacterError(input);
    var hasPatternMismatch = getValidityFlag(input, 'patternMismatch');
    var hasRangeOverflow = getValidityFlag(input, 'rangeOverflow');
    var hasRangeUnderflow = getValidityFlag(input, 'rangeUnderflow');
    var hasTooLong = getValidityFlag(input, 'tooLong');
    var hasTooShort = getValidityFlag(input, 'tooShort');
    var hasValueMissing = getValidityFlag(input, 'valueMissing');
    var hasTypeMismatch = getValidityFlag(input, 'typeMismatch');
    var shouldPrioritizePhoneRangeError = isPhoneField(input)
        && hasPhoneLengthConstraintError(input);

    if (hasPhoneParseError) {
        return $input.data('parse-error') || $input.data('pattern-mismatch');
    }

    if (shouldPrioritizePhoneRangeError && $input.data('range-error')) {
        validationMessage = $input.data('range-error');
    } else if (hasPatternMismatch && hasParseErrorMessage) {
        validationMessage = $input.data('parse-error') || $input.data('pattern-mismatch');
    }

    if ((hasRangeOverflow || hasRangeUnderflow)
        && $input.data('range-error')) {
        validationMessage = $input.data('range-error');
    }
    if ((hasTooLong || hasTooShort)
        && $input.data('range-error')) {
        validationMessage = $input.data('range-error');
    }
    if (hasValueMissing && $input.data('missing-error')) {
        validationMessage = $input.data('missing-error');
    }
    if (hasTypeMismatch && $input.data('type-mismatch')) {
        validationMessage = $input.data('type-mismatch');
    }

    return validationMessage;
}

/**
 * Validates mandatory fields in a given form element, applies invalid styles, and ensures the first invalid element is scrolled into view.
 *
 * @param {jQuery} data - The form element or container within which mandatory fields need to be validated.
 * @param {boolean} [shouldFocus=true] if true will focus on first invalid fields
 * @returns {boolean} - Returns true if all mandatory fields are valid; otherwise, returns false.
 */
function checkMandatoryField(data, shouldFocus = true) {
    var $this = data;
    var valid = true;
    var firstInvalidInput = null;

    if ($this !== undefined && $this !== '' && $this !== null) {
        $this.find('.form-control.is-invalid').removeClass('is-invalid');
        $this.find('.custom-select').removeClass('is-invalid');
        $this.find('.form-group').removeClass('is-invalid');
        $this.find('input:visible, select:visible').each((index, input) => {
            if (!input.validity.valid) {
                if (input.checkValidity && !input.checkValidity()) {
                    // Safari-specific handling
                    valid = false;
                    input.setCustomValidity('');
                    if (!input.validity.valid) {
                        var validationMessage = getValidationMessage(input);
                        $(input).addClass('is-invalid');
                        $(input).closest('.custom-select').addClass('is-invalid');
                        $(input).closest('.form-group').addClass('is-invalid');

                        $(input).parents('.form-group').find('.invalid-feedback')
                            .text(validationMessage);

                        if (!firstInvalidInput) {
                            firstInvalidInput = $(input);
                        }
                    }
                } else {
                    valid = false;
                    return false;
                }
            } else {
                var $input = $(input);
                var inputValue = $input.val() || '';
                var sanitizedValue = sanitizeInput(inputValue);
                if (sanitizedValue !== inputValue) {
                    var validationMessageNoScript = $('input[name="scriptTagNotAllowed"]').val();
                    $input.addClass('is-invalid');
                    $input.parents('.form-group').find('.invalid-feedback')
                        .text(validationMessageNoScript);
                    firstInvalidInput = $input;
                    valid = false;
                    return false;
                }

                if (isPhoneField($input) && $input.parents('.form-group').hasClass('required') && hasPhoneCharacterError($input)) {
                    var phoneParseMessage = $input.attr('data-parse-error')
                         || $input.attr('data-pattern-mismatch')
                         || $input.attr('data-value-error')
                         || $input.data('value-error');
                    $input.addClass('is-invalid');

                    $input.parents('.form-group').find('.invalid-feedback').text(phoneParseMessage || '');
                    firstInvalidInput = $input;
                    valid = false;
                    return false;
                }

                if (isPhoneField($input) && $input.parents('.form-group').hasClass('required') && hasPhoneLengthConstraintError($input)) {
                    var validationPhoneMessage = inputValue.length === 0 ? $input.attr('data-missing-error') : $input.attr('data-range-error');
                    $input.addClass('is-invalid');
                    $input.parents('.form-group').find('.invalid-feedback').text(validationPhoneMessage);
                    firstInvalidInput = $input;
                    valid = false;
                    return false;
                }
            }
            return true;
        });

        if (shouldFocus && firstInvalidInput) {
            firstInvalidInput.focus();
        }
    }

    return valid;
}

/**
* Validate whole form. Requires `this` to be set to form object
* @param {jQuery.event} event - Event to be canceled if form is invalid on blur.
* @returns {boolean} - Flag to indicate if form is valid on blur
*/
function validateFormBlur(event) {
    var valid = true;
    if (this.checkValidity && !this.checkValidity()) {
        // safari
        valid = false;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
        if (!this.validity.valid) {
            this.setCustomValidity('');
            if (!this.validity.valid) {
                var validationMessage = getValidationMessage(this);
                $(this).addClass('is-invalid');
                $(this).parent().addClass('is-invalid');
                $(this).parent('.form-group').addClass('is-invalid');
                $(this).removeClass('has-success');
                $(this).parents('.form-group').find('.invalid-feedback')
                    .text(validationMessage);
            }
        }
    } else if (isPhoneField(this) && hasPhoneCharacterError(this)) {
        var $field = $(this);
        var phoneParseMessage = $field.attr('data-parse-error')
            || $field.attr('data-pattern-mismatch')
            || $field.attr('data-value-error')
            || $field.data('value-error');
        $field.addClass('is-invalid');
        $field.parent().addClass('is-invalid');
        $field.parent('.form-group').addClass('is-invalid');
        $field.removeClass('has-success');
        $field.parents('.form-group').find('.invalid-feedback')
            .text(phoneParseMessage || '');
        valid = false;
    } else {
        $(this).removeClass('is-invalid');
        $(this).parent().removeClass('is-invalid');
        $(this).parent('.form-group').removeClass('is-invalid');
        if ($(this).val().length > 0) {
            $(this).addClass('has-success');
        } else {
            $(this).removeClass('has-success');
        }
        $(this).closest('div.form-group').find('.invalid-feedback').text('');
    }
    return valid;
}

/**
* Validates whether the email and confirm email fields match.
* Adds or removes validation classes and displays error messages as needed.
*
* @param {HTMLElement|jQuery} currEle - The confirm email input element triggering the validation.
* @param {jQuery.Event} [event] - The event object, if available, used for form validation blur handling.
*/
function validateEmailMatch(currEle, event) {
    const $emailAddress = $('.js-form-email');
    const $confirmEmail = $('.js-form-email-confirm');
    const $ele = $(currEle);

    let emailAddressVal = $emailAddress.val() || '';
    let confirmEmailVal = $confirmEmail.val() || '';

    if (!emailAddressVal || !confirmEmailVal) {
        if ($ele.hasClass('js-form-email-confirm')) {
            validateFormBlur.call(currEle, event);
        }
        return;
    }

    emailAddressVal = emailAddressVal.toLowerCase();
    confirmEmailVal = confirmEmailVal.toLowerCase();

    if (emailAddressVal !== confirmEmailVal) {
        $confirmEmail.addClass('is-invalid');
        $confirmEmail.closest('.form-group').addClass('is-invalid');
        $confirmEmail.closest('.form-group').find('.invalid-feedback').text($confirmEmail.data('emailmatch-error'));
    } else {
        $confirmEmail.closest('.form-group').find('.invalid-feedback').text('');
        $confirmEmail.removeClass('is-invalid');
        $confirmEmail.closest('.form-group').removeClass('is-invalid');
    }
}

/**
 * Remove all validation. Should be called every time before revalidating form
 * @param {element} form - Form to be cleared
 * @returns {void}
 */
function clearForm(form) {
    $(form).find('.form-control.is-invalid').removeClass('is-invalid');
    $(form).find('.form-group.is-invalid').removeClass('is-invalid');
}

/**
 * toggleError - toggles the error state of a form field based on its validity.
 * @param {jQuery} $element - The form element to toggle error on.
 * @param {boolean} isValid - Indicates whether the field is valid.
 * @param {string} dataAttribute - The data attribute containing the error message.
 */
function toggleError($element, isValid, dataAttribute) {
    if (!isValid) {
        $element.addClass('is-invalid');
        $element.closest('.form-group').addClass('is-invalid');
        $element.closest('.form-group').find('.invalid-feedback').text($element.data(dataAttribute));
    } else {
        $element.closest('.form-group').find('.invalid-feedback').text('');
        $element.removeClass('is-invalid');
        $element.closest('.form-group').removeClass('is-invalid');
    }
}

module.exports = {
    invalid: function () {
        $('body').on('invalid', 'form input, form select', function (e) {
            e.preventDefault();
            this.setCustomValidity('');
            if (!this.validity.valid) {
                var validationMessage = getValidationMessage(this);
                $(this).addClass('is-invalid');
                $(this).parents('.form-group').find('.invalid-feedback')
                    .text(validationMessage);
            }
        });
    },

    submit: function () {
        $('body').on('submit', 'form', function (e) {
            if ($(this).is('[novalidate]')) {
                return true;
            }
            return validateForm.call(this, e);
        });
    },

    buttonClick: function () {
        $('form button[type="submit"], form input[type="submit"]').on('click', function () {
            // clear all errors when trying to submit the form
            clearForm($(this).parents('form'));
        });
    },

    functions: {
        validateForm: function (form, event) {
            validateForm.call($(form), event || null);
        },
        clearForm: clearForm
    },

    getValidationMessage: getValidationMessage,
    checkMandatoryField: checkMandatoryField,

    focusoutValidation: function () {
        $('body').on('blur', 'input, select', function (e) {
            const $field = $(this);

            if ($field.hasClass('js-form-email-confirm') || $field.hasClass('js-form-email')) {
                validateFormBlur.call(this, e);
            }
        });
    },

    validateFormFill: function () {
        $('body').on('validateFormFill', function () {
            $('.form-group .invalid-feedback').text('');
            $('.form-group .is-invalid').removeClass('is-invalid');
            $('input, select').each(function () {
                if ($(this).val()) {
                    validateFormBlur.call(this);
                }
            });
        });
    },

    phoneNoValidation: function (phoneNumber, pattern) {
        // Only allow digits, space, dash, parentheses, dot — NO @#$%&*
        const allowedCharsOnly = /^[0-9()\s.-]+$/;
        if (pattern) {
            const isValidPattern = new RegExp(pattern, 'gi');
            return allowedCharsOnly.test(phoneNumber) && isValidPattern.test(phoneNumber);
        }
        // Validate phone format (US)
        const phoneFormat = /^(?:\+1\s?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/;

        var isValid = allowedCharsOnly.test(phoneNumber) && phoneFormat.test(phoneNumber);
        return isValid;
    },

    nameValidation: function (name, length, pattern) {
        if ((name === null || name === undefined) || length < 0) {
            return false;
        }
        var isValidLength = name.trim().length >= length;
        var isValidChars = /\*|\?/.test(name) === false;
        var isValidPattern = true;
        if (pattern) {
            isValidPattern = new RegExp(pattern, 'gi').test(name);
        }
        var isValid = isValidLength && isValidChars && isValidPattern;
        return isValid;
    },

    checkEmailMatch: function () {
        $('body').on('blur change', '.js-form-email, .js-form-email-confirm', function (e) {
            validateEmailMatch(this, e);
        });
    },

    checkPasswordMatch: function ($pwd, $confirmPwd) {
        if (!$pwd || !$confirmPwd || $pwd.length === 0 || $confirmPwd.length === 0 || $pwd.val().length === 0 || $confirmPwd.val().length === 0) {
            return false;
        }
        var isValid = $pwd.val() === $confirmPwd.val();
        toggleError($confirmPwd, isValid, 'mismatch-error');
        return isValid;
    },

    validateEmail: function ($email) {
        if (!$email || $email.length === 0 || $email.val().length === 0) {
            return false;
        }
        var regex = /^[\w.%+-]+@[\w.-]+\.\w{2,}$/;
        var isValid = regex.test($email.val());
        toggleError($email, isValid, 'invalid-error');
        return isValid;
    }
};
